import React, { useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
} from '@mui/material';

import SnapshotList from '../snapshots/SnapshotList';

import { cleanSnapshotSettings } from '../../utils/snapshotUtils';
import { Snapshot } from 'types';
import { CleanedSnapshot } from './types';

import { useSnapshots } from 'features/snapshots/api/getSnapshots';
import { useAnalyzer } from 'features/analyzers/api/getAnalyzer';
import { useSnapshotFilterBar } from 'features/snapshots/SnapshotFilterBar';
import { zonedTimeToUtc } from 'date-fns-tz';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';

interface Props {
  selectedAnalyzerId: number;
  open: boolean;
  handleClose: () => void;
}

const SnapshotExportDialog = ({
  selectedAnalyzerId,
  open,
  handleClose,
}: Props) => {
  const [selectedSnapshotIds, setSelectedSnapshotIds] = useState<number[]>([]);

  const { data: analyzer } = useAnalyzer({ analyzerId: selectedAnalyzerId });
  const { data: snapshots } = useSnapshots({ analyzerId: selectedAnalyzerId });

  const { SnapshotFilterBar, getFilterSnapshotsByName } =
    useSnapshotFilterBar();
  const { timezone } = useUserDateTime();

  if (analyzer === undefined || snapshots === undefined) return null;

  const checkIfSnapshotSelected = (snapshot: Snapshot) => {
    return selectedSnapshotIds.indexOf(snapshot.id) > -1;
  };

  const handleSnapshotChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    if (checked) {
      setSelectedSnapshotIds([...selectedSnapshotIds, parseInt(name)]);
    } else {
      const filteredSnapshots = selectedSnapshotIds.filter(
        (snapshotId) => snapshotId !== parseInt(name)
      );
      setSelectedSnapshotIds(filteredSnapshots);
    }
  };

  const handleCheckAllSnapshots = (checked: boolean) => {
    const allSnapshotIds = snapshots.map((snapshot) => snapshot.id);
    const snapshotsToCheck = checked ? allSnapshotIds : [];
    setSelectedSnapshotIds(snapshotsToCheck);
  };

  const exportSnapshots = () => {
    const selectedSnapshots = snapshots.filter(
      (snapshot) => selectedSnapshotIds.indexOf(snapshot.id) > -1
    );

    const cleanedSnapshots: CleanedSnapshot[] = [];
    for (let i = 0; i < selectedSnapshots.length; i++) {
      const cleanedSnapshot = cleanSnapshotSettings(selectedSnapshots[i]);
      cleanedSnapshots.push(cleanedSnapshot);
    }

    const element = document.createElement('a');
    const file = new Blob([JSON.stringify(cleanedSnapshots)], {
      type: 'text/plain',
    });
    element.href = URL.createObjectURL(file);
    element.download = `${analyzer.name}-snapshots-export-${zonedTimeToUtc(
      new Date(),
      timezone
    )
      .toISOString()
      .slice(0, 10)}`;
    document.body.appendChild(element);
    element.click();
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={{ zIndex: 99999999 }}>
      <DialogTitle>Export Selected Snapshots</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Select the snapshots you would like to export to an external file.
        </DialogContentText>
        <Grid sx={{ mt: '.5rem' }} justifyContent='flex-start' container>
          {SnapshotFilterBar}
        </Grid>
        <SnapshotList
          analyzerId={selectedAnalyzerId}
          checkIfSnapshotSelected={checkIfSnapshotSelected}
          handleSnapshotChecked={handleSnapshotChecked}
          enableCheckAll
          handleCheckAllSnapshots={handleCheckAllSnapshots}
          getFilterSnapshots={getFilterSnapshotsByName}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant='contained' color='secondary'>
          Cancel
        </Button>
        <Button
          disabled={selectedSnapshotIds.length <= 0}
          onClick={exportSnapshots}
          variant='contained'
          color='primary'
        >
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SnapshotExportDialog;
